// src/context/SocketContext.js - VERSIÓN CORREGIDA SIN /api
import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import authService from '../services/authService';
import { debugLog } from '../utils/debugLogger';

const SocketContext = createContext(null);
const normalizeSocketUrl = (rawApiUrl) => {
  const clean = (rawApiUrl || '').toString().trim().replace(/\/+$/, '');
  if (!clean) return '';
  return clean.replace(/\/api$/i, '');
};

const resolveSocketUrl = () => {
  const envUrl = normalizeSocketUrl(process.env.REACT_APP_API_URL);
  if (envUrl) return envUrl;

  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const capacitor = window.Capacitor;
  const isNativeApp = typeof capacitor?.isNativePlatform === 'function'
    ? capacitor.isNativePlatform()
    : (typeof capacitor?.getPlatform === 'function' && capacitor.getPlatform() !== 'web');

  if (isLocalhost && !isNativeApp) return 'http://localhost:5000';
  return 'https://gestion.smtlinkss.com';
};

export function SocketProvider({ children }) {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) {
      setSocket((currentSocket) => {
        if (currentSocket) {
          debugLog('💤 Usuario deslogueado, desconectando socket...');
          currentSocket.disconnect();
        }
        return null;
      });
      setConnected(false);
      return;
    }

    // ✅ CORRECCIÓN: Remover /api del final para Socket.IO
    const token = authService.getToken();
    const socketUrl = resolveSocketUrl();
    
    debugLog('🔌 Conectando Socket.IO a:', socketUrl);
    
    const newSocket = io(socketUrl, {
      auth: {
        token: token
      },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      // Fall back to authenticated HTTP polling if the proxy rejects WebSocket.
      tryAllTransports: true,
      transports: ['websocket', 'polling']
    });

    newSocket.on('connect', () => {
      debugLog('✅ WebSocket conectado:', newSocket.id);
      setConnected(true);
      
      // SOLO emitir user_joined (evento que SÍ existe en backend)
      newSocket.emit('user_joined', {
        userId: user.id,
        username: user.username || user.name || 'Usuario'
      });
    });

    newSocket.on('disconnect', (reason) => {
      debugLog('❌ WebSocket desconectado. Razón:', reason);
      setConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('❌ Error de conexión WebSocket:', error.message);
      setConnected(false);
    });

    newSocket.on('reconnect', (attemptNumber) => {
      debugLog(`🔄 WebSocket reconectado después de ${attemptNumber} intento(s)`);
      setConnected(true);
      
      // Re-enviar user_joined tras reconexión
      newSocket.emit('user_joined', {
        userId: user.id,
        username: user.username || user.name || 'Usuario'
      });
    });

    setSocket(newSocket);

    return () => {
      if (newSocket) {
        debugLog('🔌 Cerrando conexión WebSocket...');
        newSocket.disconnect();
      }
    };
  }, [user]);

  const value = {
    socket,
    connected
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket debe usarse dentro de SocketProvider');
  }
  return context.socket; 
}

export function useSocketContext() {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocketContext debe usarse dentro de SocketProvider');
  }
  return context;
}

export { SocketContext };
